<script setup lang="ts">
// Maps one nav section/child to an <e-book-menu-item> for the mega-menu's
// `slot="mobile"` drawer — same data the desktop dropdowns use, but as an
// expandable tree with icons. A node with children renders as a
// collapsible header (no `to`); a leaf links to its path.
interface NavNode {
  slug?: string;
  title: string;
  description?: string;
  icon?: string;
  path: string;
  children?: NavNode[];
  columns?: { items?: NavNode[] }[];
  tabs?: { key: string; label: string; icon?: string; columns?: { items?: NavNode[] }[] }[];
  // A non-interactive visual label (e-book-menu-item renders a header when it
  // has no `to` and no children). Used for flattened tab groups.
  isHeader?: boolean;
  // A leaf sitting under a tab-group header — indented so it reads as belonging
  // to that label rather than a sibling of it.
  grouped?: boolean;
}

const props = defineProps<{ node: NavNode }>();

// Fade + lock leaf links the visitor can't reach yet. Host is
// `display:contents`, so opacity targets the inner `link`/`label` parts.
const { isMuted } = useCanAccess();

const children = computed<NavNode[]>(() => {
  const n = props.node;
  // Tabbed sections: flatten to a non-interactive header label per tab followed
  // by that tab's items, all as flat siblings — the tab is a visual label, not
  // an expandable accordion, and its items sit under it.
  if (n.tabs?.length) {
    return n.tabs.flatMap((t) => [
      { title: t.label, icon: t.icon, path: `${n.path}#${t.key}`, isHeader: true },
      ...(t.columns?.flatMap((c) => (c.items ?? []).map((i) => ({ ...i, grouped: true }))) ?? []),
    ]);
  }
  // Sections group their children into `columns` for the desktop dropdown;
  // flatten those for the tree. Otherwise use direct children (drop self-index).
  if (n.columns?.length) return n.columns.flatMap((c) => c.items ?? []);
  return (n.children ?? []).filter((c) => c.path !== n.path);
});
const hasChildren = computed(() => children.value.length > 0);
const isHeader = computed(() => props.node.isHeader === true);
// A leaf the visitor can't reach yet — but never a header (it isn't a link).
const locked = computed(() => !isHeader.value && !hasChildren.value && isMuted(props.node.path));

const hostClass = computed(() => {
  const cls: string[] = [];
  if (isHeader.value) cls.push("nav-label");
  if (props.node.grouped) cls.push("nav-grouped");
  if (locked.value) cls.push("part-[link]:opacity-60", "part-[label]:opacity-60");
  return cls.length ? cls.join(" ") : undefined;
});

// <e-book-menu-item> collapses its children via `grid-template-rows: 0fr`, but
// the inner block's `mt-0.5 mb-1` margins still count toward the grid's `auto`
// track minimum, so a *closed* group keeps a 6px height. The host never
// reflects `open` (only an internal ref flips), and `data-open` lives on the
// wrapper inside the shadow — unreachable via `::part`. Inject a sheet into the
// item's own shadow root, where the attribute selector works, to zero those
// margins while collapsed. Restored on open, so expanded spacing is unchanged.
const rootEl = ref<HTMLElement | null>(null);
let collapseFixSheet: CSSStyleSheet | null = null;
function collapseSheet(): CSSStyleSheet {
  if (!collapseFixSheet) {
    collapseFixSheet = new CSSStyleSheet();
    collapseFixSheet.replaceSync(
      '[part="children"]:not([data-open="true"]) > * { margin-top: 0; margin-bottom: 0; }',
    );
  }
  return collapseFixSheet;
}
onMounted(async () => {
  if (!hasChildren.value) return;
  await customElements.whenDefined("e-book-menu-item");
  const root = rootEl.value?.shadowRoot;
  if (!root) return;
  const sheet = collapseSheet();
  if (!root.adoptedStyleSheets.includes(sheet)) {
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
  }
});
</script>

<template>
  <e-book-menu-item
    ref="rootEl"
    :label="node.title"
    :icon="isHeader ? undefined : locked ? 'lock' : node.icon || undefined"
    :to="isHeader || hasChildren ? undefined : node.path"
    :open.attr="hasChildren ? 'false' : undefined"
    :class="hostClass"
  >
    <MegaMobileNavItem
      v-for="child in children"
      :key="child.path"
      slot="children"
      :node="child"
    />
  </e-book-menu-item>
</template>
